import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, FileText, UploadSimple, DownloadSimple, Warning, CheckCircle, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  valida:       { label: "Valida",        cls: "bg-green-50 text-green-700 border-green-200",    icon: CheckCircle },
  in_scadenza:  { label: "In scadenza",   cls: "bg-orange-50 text-orange-700 border-orange-200", icon: Clock },
  scaduta:      { label: "Scaduta",       cls: "bg-red-50 text-red-700 border-red-200",          icon: Warning },
  permanente:   { label: "Senza scadenza",cls: "bg-blue-50 text-blue-700 border-blue-200",       icon: CheckCircle },
  sconosciuto:  { label: "—",             cls: "bg-gray-50 text-gray-600 border-gray-200",       icon: Clock },
};

export default function EmployeeCertifications({ employeeId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", issuing_body: "", issued_date: "", expiry_date: "", no_expiry: false, notes: "" });

  const load = () => {
    if (!employeeId) return;
    axios.get(`${API}/employees/${employeeId}/certifications-v2`, { withCredentials: true })
      .then(r => setItems(r.data?.items || [])).catch(() => setItems([]));
  };
  useEffect(load, [employeeId]);

  const reset = () => setForm({ name: "", issuing_body: "", issued_date: "", expiry_date: "", no_expiry: false, notes: "" });

  const create = async () => {
    if (!form.name.trim()) { toast.error("Nome certificazione obbligatorio"); return; }
    if (!form.no_expiry && !form.expiry_date) { toast.error("Data scadenza obbligatoria"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/certifications-v2`, form, { withCredentials: true });
      toast.success("Certificazione aggiunta");
      setOpen(false); reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const remove = async (cid, name) => {
    if (!window.confirm(`Spostare "${name}" nel cestino? Ripristinabile entro 30 giorni.`)) return;
    try {
      await axios.delete(`${API}/certifications-v2/${cid}`, { withCredentials: true });
      toast.success("Spostata nel cestino"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const upload = async (cid, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5MB)"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/certifications-v2/${cid}/upload`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); }
  };

  const download = async (cid, filename) => {
    try {
      const r = await axios.get(`${API}/certifications-v2/${cid}/document`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || `cert_${cid}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  return (
    <div className="border border-[#0D1F3C]/8 rounded-lg p-4" data-testid="emp-certifications-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#0D1F3C]">Certificazioni e Scadenze</h3>
          <p className="text-xs text-[#0D1F3C]/50">Patentini, attestati sicurezza, lingue, RSPP, muletto…</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="add-cert-btn">
              <Plus size={14} className="mr-1" />Aggiungi
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nuova Certificazione</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div><Label className="text-xs mb-1 block">Nome certificazione *</Label>
                <Input data-testid="cert-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="es. Patentino muletto" />
              </div>
              <div><Label className="text-xs mb-1 block">Ente rilasciante</Label>
                <Input data-testid="cert-issuer" value={form.issuing_body} onChange={e => setForm(p => ({ ...p, issuing_body: e.target.value }))} placeholder="es. INAIL" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block">Data rilascio</Label>
                  <Input type="date" value={form.issued_date} onChange={e => setForm(p => ({ ...p, issued_date: e.target.value }))} />
                </div>
                <div><Label className="text-xs mb-1 block">Data scadenza {!form.no_expiry && "*"}</Label>
                  <Input data-testid="cert-expiry" type="date" disabled={form.no_expiry} value={form.expiry_date} onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="no-expiry" checked={form.no_expiry} onCheckedChange={v => setForm(p => ({ ...p, no_expiry: !!v, expiry_date: v ? "" : p.expiry_date }))} data-testid="cert-no-expiry" />
                <Label htmlFor="no-expiry" className="text-xs cursor-pointer">Senza scadenza (es. attestato permanente)</Label>
              </div>
              <div><Label className="text-xs mb-1 block">Note</Label>
                <Textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <Button onClick={create} disabled={busy} className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="cert-save-btn">
                {busy ? "Salvataggio…" : "Aggiungi"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#0D1F3C]/40 italic py-4 text-center">Nessuna certificazione registrata</p>
      ) : (
        <div className="space-y-2">
          {items.map(c => {
            const meta = STATUS_META[c.status_calc] || STATUS_META.sconosciuto;
            const Icon = meta.icon;
            return (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-md border border-[#0D1F3C]/8 bg-white" data-testid={`cert-row-${c.id}`}>
                <Icon size={20} weight="duotone" className={c.status_calc === "scaduta" ? "text-red-500" : c.status_calc === "in_scadenza" ? "text-orange-500" : "text-green-600"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-[#0D1F3C] truncate">{c.name}</p>
                    <Badge className={`text-[10px] border ${meta.cls}`} data-testid={`cert-badge-${c.id}`}>{meta.label}</Badge>
                  </div>
                  <p className="text-xs text-[#0D1F3C]/50">
                    {c.issuing_body || "—"}
                    {c.expiry_date && ` · Scadenza: ${new Date(c.expiry_date).toLocaleDateString("it-IT")}`}
                    {c.days_to_expiry !== null && c.days_to_expiry !== undefined && c.status_calc === "in_scadenza" && (
                      <span className="font-semibold text-orange-700"> · tra {c.days_to_expiry} giorni</span>
                    )}
                    {c.days_to_expiry !== null && c.days_to_expiry !== undefined && c.status_calc === "scaduta" && (
                      <span className="font-semibold text-red-700"> · scaduta da {Math.abs(c.days_to_expiry)} giorni</span>
                    )}
                  </p>
                </div>
                {c.document_file_id ? (
                  <Button variant="ghost" size="sm" onClick={() => download(c.id, c.document_filename)} title="Scarica documento" data-testid={`cert-download-${c.id}`}>
                    <DownloadSimple size={14} />
                  </Button>
                ) : (
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => upload(c.id, e.target.files[0])} />
                    <Button variant="ghost" size="sm" asChild title="Carica PDF/JPG/PNG"><span data-testid={`cert-upload-${c.id}`}><UploadSimple size={14} /></span></Button>
                  </label>
                )}
                <Button variant="ghost" size="sm" onClick={() => remove(c.id, c.name)} title="Sposta nel cestino" data-testid={`cert-delete-${c.id}`} className="text-red-500 hover:text-red-700">
                  <Trash size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
